import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Search as SearchIcon, X } from 'lucide-react';
import hotelService from '../../services/hotelService';
import './HotelChooser.css';

// CHOOSING THE HOTEL WHERE THE HOTEL IS MISSING.
//
// This screen is meaningless without a property — the routes, the credentials and the gateway url are
// all resolved in its context — and it used to say so and stop, pointing at the picker in the top bar.
// That is an instruction to go and use a different control, which the reader then has to find, and it
// leaves the one thing they came here to do two clicks away in a menu they were not looking at.
// The list they need is small and already fetchable, so it belongs here.
//
// THE TOP BAR STILL OWNS THE SELECTION. This does not keep a hotel of its own; it writes through to the
// same context the picker writes to, so the breadcrumb, every other screen and a page refresh all agree.
//
// SCOPING FOLLOWS HP-75: to the chosen chain when there is one, otherwise to the chosen partner. An
// unscoped call returns every hotel the caller can see, which for a partner user means other partners'
// properties appearing in a list that looks like their own.
const HotelChooser = ({ selectedAccount, onPick, title, hint }) => {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    // A CHAIN IS CHOSEN FIRST, AND UNTIL IT IS THERE IS NO LIST.
    //
    // Unscoped, this asked for the first 200 hotels the caller could see and offered them as one flat
    // list. Every property on the platform reads as equally likely, the names carry no chain, and the
    // list is truncated at a number the reader has no way to know is short — so the hotel they want may
    // simply not be there. Picking from it then silently switched the chain in the top bar underneath
    // them.
    //
    // The chain is a decision the reader can actually make, and the top bar already exists to make it.
    // Nothing is fetched until it has been: a list nobody should choose from is not worth loading.
    const load = useCallback((term) => {
        if (!selectedAccount) { setHotels([]); setLoading(false); return undefined; }
        setLoading(true);
        setError(null);
        const filters = { accountid: selectedAccount };
        if (term) filters.search = term;
        return hotelService.getAllHotels(filters)
            .then((data) => setHotels(data.hotels || data || []))
            // An empty list and a list that failed to load look identical and mean opposite things, so
            // they are never allowed to render the same words.
            .catch(() => setError('Could not load the hotel list.'))
            .finally(() => setLoading(false));
    }, [selectedAccount]);

    useEffect(() => { load(''); }, [load]);

    // Filtered in the browser, not at the endpoint: a chain's hotels all arrive in one answer, so the
    // list here is complete and "no hotel matches" is the truth rather than an artefact of a page size.
    const q = search.trim().toLowerCase();
    const shown = q
        ? hotels.filter(h => (h.hotelname || '').toLowerCase().includes(q)
                          || (h.hotelcode || '').toLowerCase().includes(q))
        : hotels;

    // The empty state names the control to use and what it looks like, because "choose a chain" sends
    // someone hunting along a top bar they were not looking at.
    if (!selectedAccount) {
        return (
            <div className="otx-table-wrap uapi-hotel-chooser">
                <div className="uapi-hotel-chooser-head">
                    <Building2 size={20} className="uapi-hotel-chooser-icon" />
                    <div>
                        <h2 className="uapi-hotel-chooser-title">Choose a hotel chain</h2>
                        <p className="uapi-hotel-chooser-hint">
                            Use <strong>Select Hotel Chain</strong> in the top bar. Its hotels are listed here
                            next, and the routes, credentials and gateway url are then shown for the one you
                            pick.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="otx-table-wrap uapi-hotel-chooser">
            <div className="uapi-hotel-chooser-head">
                <Building2 size={20} className="uapi-hotel-chooser-icon" />
                <div>
                    <h2 className="uapi-hotel-chooser-title">{title || 'Choose a hotel'}</h2>
                    <p className="uapi-hotel-chooser-hint">
                        {hint || 'The routes, credentials and gateway url are shown for one property. The picker in the top bar changes it later.'}
                    </p>
                </div>
            </div>

            {/* ALWAYS SHOWN. It was hidden under a threshold on the theory that a control between the
                reader and a list they can already see is noise, which was wrong: a short list is exactly
                where someone types rather than reads. */}
            <div className="lu-cred-control uapi-hotel-search">
                <SearchIcon size={14} className="lu-cred-icon" />
                <input
                    className="form-input lu-cred-input"
                    placeholder="Search by name or code…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                />
                {search && (
                    <button type="button" className="uapi-hotel-search-clear" title="Clear" onClick={() => setSearch('')}>
                        <X size={13} />
                    </button>
                )}
            </div>

            {loading && <div className="uapi-hotel-chooser-msg">Loading hotels…</div>}
            {!loading && error && <div className="uapi-hotel-chooser-msg uapi-hotel-chooser-error">{error}</div>}
            {!loading && !error && hotels.length === 0 && (
                <div className="uapi-hotel-chooser-msg">No hotels are available to you in this chain.</div>
            )}
            {!loading && !error && hotels.length > 0 && shown.length === 0 && (
                <div className="uapi-hotel-chooser-msg">No hotel matches “{search.trim()}”.</div>
            )}

            {!loading && shown.length > 0 && (
                <ul className="uapi-hotel-list">
                    {shown.map(h => (
                        <li key={h.hotelid}>
                            <button type="button" className="uapi-hotel-item" onClick={() => onPick(h)}>
                                <span className="uapi-hotel-name">{h.hotelname || 'Unnamed Hotel'}</span>
                                {h.hotelcode && <span className="uapi-hotel-code">{h.hotelcode}</span>}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default HotelChooser;
